import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Autocomplete, useLoadScript } from '@react-google-maps/api';
import { Input, Alert } from 'antd';
import type { InputProps } from 'antd';
import type { ParsedGooglePlace } from '../utils/addressUtils';
import { parseGooglePlace, parseNewGooglePlace } from '../utils/addressUtils';

const libraries: 'places'[] = ['places'];

type PlaceAutocompleteCtor = new (opts?: Record<string, unknown>) => HTMLElement & { value?: string };

/**
 * After `importLibrary('places')`, `PlaceAutocompleteElement` lives on `google.maps.places`,
 * not on the object returned by `importLibrary` (which often has no `PlaceAutocompleteElement`).
 */
async function getPlaceAutocompleteConstructor(): Promise<PlaceAutocompleteCtor | null> {
  const g = (window as { google?: { maps?: { importLibrary?: (name: string) => Promise<unknown>; places?: { PlaceAutocompleteElement?: unknown } } } })
    .google?.maps;
  if (!g || typeof g.importLibrary !== 'function') return null;
  try {
    const lib = await g.importLibrary('places');
    const fromNs = g.places?.PlaceAutocompleteElement;
    const fromLib = (lib as { PlaceAutocompleteElement?: unknown } | null)?.PlaceAutocompleteElement;
    const Ctor = (typeof fromNs === 'function' ? fromNs : fromLib) as PlaceAutocompleteCtor | undefined;
    return typeof Ctor === 'function' ? Ctor : null;
  } catch {
    return null;
  }
}

export interface AddressAutocompleteInputProps extends Omit<InputProps, 'onChange'> {
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onPlaceResolved?: (place: ParsedGooglePlace) => void;
}

/** Legacy `google.maps.places.Autocomplete` (classic .pac-container). */
const LegacyAutocompleteInput: React.FC<AddressAutocompleteInputProps> = ({
  onPlaceResolved,
  onChange,
  value,
  ...inputProps
}) => {
  const acRef = useRef<any>(null);

  const onLoad = useCallback((autocomplete: any) => {
    acRef.current = autocomplete;
    autocomplete.setFields(['address_components', 'formatted_address', 'geometry', 'name']);
  }, []);

  const onPlaceChanged = useCallback(() => {
    const place = acRef.current?.getPlace();
    if (!place || !place.address_components?.length) return;
    const parsed = parseGooglePlace(place);
    onPlaceResolved?.(parsed);
    onChange?.({ target: { value: parsed.streetLine } } as React.ChangeEvent<HTMLInputElement>);
  }, [onChange, onPlaceResolved]);

  return (
    <Autocomplete onLoad={onLoad} onUnmount={() => (acRef.current = null)} onPlaceChanged={onPlaceChanged} options={{ types: ['address'] }}>
      <Input {...inputProps} value={value} onChange={onChange} />
    </Autocomplete>
  );
};

/**
 * New Places UI Kit: `PlaceAutocompleteElement` (gmp-place-autocomplete).
 * Uses `importLibrary('places')` + `Place.fetchFields`. Falls back to legacy widget if unavailable.
 */
const NewPlaceAutocompleteElementInput: React.FC<AddressAutocompleteInputProps> = (props) => {
  const { onPlaceResolved, onChange, value, ...inputProps } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef<(HTMLElement & { value?: string }) | null>(null);
  const propsRef = useRef({ onPlaceResolved, onChange, placeholder: inputProps.placeholder, value });
  propsRef.current = { onPlaceResolved, onChange, placeholder: inputProps.placeholder, value };

  const [elementMounted, setElementMounted] = useState(false);
  const [useLegacyFallback, setUseLegacyFallback] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof window === 'undefined') return;

    let cancelled = false;
    let cleanup: (() => void) | undefined;

    (async () => {
      try {
        const PlaceCtor = await getPlaceAutocompleteConstructor();
        if (cancelled || !container) return;
        if (!PlaceCtor) {
          setUseLegacyFallback(true);
          return;
        }

        const el = new PlaceCtor({
          placeholder: propsRef.current.placeholder || 'Enter street address',
        });
        (el as HTMLElement).style.width = '100%';
        el.setAttribute('requested-language', 'en');

        const onGmpSelect = async (event: Event) => {
          const pred = (event as CustomEvent & { placePrediction?: { toPlace: () => unknown } }).placePrediction;
          if (!pred) return;
          try {
            const place = pred.toPlace() as {
              fetchFields: (o: { fields: string[] }) => Promise<void>;
            };
            await place.fetchFields({
              fields: ['addressComponents', 'formattedAddress', 'location'],
            });
            const parsed = parseNewGooglePlace(place as any);
            propsRef.current.onPlaceResolved?.(parsed);
            propsRef.current.onChange?.({
              target: { value: parsed.streetLine },
            } as React.ChangeEvent<HTMLInputElement>);
          } catch (err) {
            console.error('PlaceAutocompleteElement: fetchFields failed', err);
          }
        };

        el.addEventListener('gmp-select', onGmpSelect as EventListener);
        container.appendChild(el);
        elementRef.current = el;
        const v = propsRef.current.value;
        if (v !== undefined && v !== '') {
          try {
            el.value = v;
          } catch {
            /* ignore */
          }
        }
        setElementMounted(true);

        cleanup = () => {
          el.removeEventListener('gmp-select', onGmpSelect as EventListener);
          el.remove();
          elementRef.current = null;
          setElementMounted(false);
        };
      } catch (err) {
        console.error('PlaceAutocompleteElement: failed to create', err);
        if (!cancelled) setUseLegacyFallback(true);
      }
    })();

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, []);

  useEffect(() => {
    const el = elementRef.current;
    if (!elementMounted || !el || value === undefined) return;
    try {
      el.value = value;
    } catch {
      /* ignore */
    }
  }, [value, elementMounted]);

  if (useLegacyFallback) {
    return <LegacyAutocompleteInput {...props} />;
  }

  return <div ref={containerRef} className="patel-gmp-place-autocomplete-host" style={{ width: '100%' }} />;
};

const InnerAddressInput: React.FC<AddressAutocompleteInputProps> = (props) => {
  const [mode, setMode] = useState<'checking' | 'new' | 'legacy'>('checking');

  useEffect(() => {
    let active = true;
    (async () => {
      const ctor = await getPlaceAutocompleteConstructor();
      if (!active) return;
      setMode(ctor ? 'new' : 'legacy');
    })();
    return () => {
      active = false;
    };
  }, []);

  if (mode === 'checking') {
    return <Input {...props} disabled placeholder={props.placeholder || 'Loading address search…'} />;
  }
  if (mode === 'legacy') {
    return <LegacyAutocompleteInput {...props} />;
  }
  return <NewPlaceAutocompleteElementInput {...props} />;
};

const AddressAutocompleteInput: React.FC<AddressAutocompleteInputProps> = (props) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    return <Input {...props} />;
  }
  return <AddressAutocompleteWithScript {...props} />;
};

const AddressAutocompleteWithScript: React.FC<AddressAutocompleteInputProps> = (props) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY!;
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
    version: 'weekly',
  });

  if (loadError) {
    return (
      <div style={{ width: '100%' }}>
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 8 }}
          message="Address search unavailable"
          description="Google Maps script failed to load (check API key, Places API, billing, and HTTP referrer restrictions). You can still type the address manually."
        />
        <Input {...props} placeholder={props.placeholder || 'Enter street address'} />
      </div>
    );
  }
  if (!isLoaded) {
    return <Input {...props} disabled placeholder="Loading address search…" />;
  }
  return <InnerAddressInput {...props} />;
};

export default AddressAutocompleteInput;
